import React from 'react';
import { Grid, Box, useMediaQuery } from '@mui/material';
import CustomAccordion from '../ui/CustomAccordion';
import InfoField from './InfoField';

interface CaregiverInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  gender?: string;
  address?: string;
}

interface CaregiverSectionProps {
  caregiver?: CaregiverInfo;
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  fieldConfig?: Array<{ key: string; label: string }>;
  headerText?: string;
}

const CaregiverSection: React.FC<CaregiverSectionProps> = ({
  caregiver,
  expandedSection,
  setExpandedSection,
  fieldConfig,
  headerText = 'Caregiver Information',
}) => {
  // Custom breakpoint: 4 columns only when screen width > 1200px
  const isLargeScreen = useMediaQuery('(min-width:1200px)');

  // Don't render if caregiver is null/undefined or has no meaningful data
  if (!caregiver) return null;

  // Check if caregiver object has any non-empty values
  const hasData = Object.values(caregiver).some(
    (value) => value !== undefined && value !== null && value !== ''
  );

  if (!hasData) return null;

  // Default fields if not provided by Sitecore
  const defaultFields = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'gender', label: 'Gender' },
    { key: 'address', label: 'Address' },
  ];

  const fields = fieldConfig && fieldConfig.length > 0 ? fieldConfig : defaultFields;

  return (
    <CustomAccordion
      title={headerText}
      expanded={expandedSection === 'caregiver'}
      onChange={() => setExpandedSection(expandedSection === 'caregiver' ? null : 'caregiver')}
      sx={{ my: 1.25 }}
      detailsSx={{ p: 1.25 }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={1.25}>
          {fields.map((field) => (
            <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={field.key}>
              <InfoField label={field.label} value={(caregiver as any)[field.key]} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </CustomAccordion>
  );
};

export default CaregiverSection;
